import { ButtonRole, ButtonSize } from "@components/Button";
import { Card } from "@components/Card";
import { Icon, IconDisplay, IconSize } from "@components/Icon";
import {
  getSupportNonprofitShareData,
  ShareButton,
} from "@components/ShareButton";
import styled from "@emotion/styled";

import {
  getRoutePath,
  ClientRouteName,
  URLFormat,
} from "@every.org/common/src/helpers/clientRoutes";

import { ContextNonprofit } from "src/context/NonprofitsContext/types";
import { horizontalStackCss, verticalStackCss } from "src/theme/spacing";
import { textSizeCss } from "src/theme/text";
import { ClickAction } from "src/utility/analytics";
import { nonprofitShareImageCloudinaryParams } from "src/utility/cloudinary/nonprofit";
import {
  INSTAGRAM_POST_DIMENSIONS,
  DEFAULT_SHARE_IMAGE_CLOUDINARY_PARAMS,
} from "src/utility/opengraph";

const StyledCard = styled(Card)`
  ${verticalStackCss.l};
  max-width: 688px;
`;

interface ShareProfileCardProps {
  nonprofit: ContextNonprofit;
  className?: string;
}

export function ShareProfileCard({
  nonprofit,
  className,
}: ShareProfileCardProps) {
  const shareButton = (
    <ShareButton
      aria-label="Share suggested nonprofit"
      data-tname="shareSuggestedNonprofitButton"
      data-action={ClickAction.SHARE}
      role={ButtonRole.SECONDARY}
      size={ButtonSize.MEDIUM}
      hideIcon
      hideText
      css={{ alignSelf: "flex-end" }}
      contentCss={[
        horizontalStackCss.xs,
        { alignItems: "center", justifyContent: "center" },
      ]}
      shareData={(medium) =>
        getSupportNonprofitShareData({
          medium,
          nonprofit,
          body: nonprofit?.description || undefined,
          url: getRoutePath({
            name: ClientRouteName.NONPROFIT_OR_CAUSE,
            tokens: { nonprofitSlug: nonprofit.primarySlug },
            format: URLFormat.ABSOLUTE,
          }),
        })
      }
      instagramImageCloudinaryParams={
        nonprofitShareImageCloudinaryParams({
          logoCloudinaryId: nonprofit.logoCloudinaryId,
          coverImageCloudinaryId: nonprofit.coverImageCloudinaryId,
          imageHeight: INSTAGRAM_POST_DIMENSIONS.height,
        }) || DEFAULT_SHARE_IMAGE_CLOUDINARY_PARAMS
      }
    >
      <Icon
        iconImport={() => import("@components/Icon/icons/ShareIcon")}
        size={IconSize.SMALL}
        display={IconDisplay.CURRENT_COLOR}
      />
      <span> Share Profile</span>
    </ShareButton>
  );

  return (
    <StyledCard>
      <p css={textSizeCss.m}>Do you know someone at {nonprofit.name}?</p>
      <p>
        They haven’t created their profile yet. Share this page with someone who
        works there so they can get set up.
      </p>
      {shareButton}
    </StyledCard>
  );
}
